import type { ReactElement } from 'react';
import { useContext, useEffect, useRef } from 'react';

import type { CompositionFeType } from './utils';

import { PlusIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';

import { RawMaterialRow } from './RawMaterialRow';
import { MaterialOnTheFlyValueContext } from './reducer';

interface RawMaterialsSectionProps {
    isRequired: boolean;
}

const RawMaterialsSection = ({
    isRequired,
}: RawMaterialsSectionProps): ReactElement | null => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { materialOnTheFlyValue, dispatchMaterialOnTheFly } = useContext(
        MaterialOnTheFlyValueContext
    );

    const disableDelete = materialOnTheFlyValue.compositions.length === 1;
    const disableAddRawMaterial = !materialOnTheFlyValue.materialSubcategory;

    const totalPercentage = [...materialOnTheFlyValue.compositions].reduce(
        (acc, c) => acc + (c.percentage ?? 0),
        0
    );

    const isRawMaterialsFilled = materialOnTheFlyValue.compositions.every(
        (composition) => composition.rawMaterial
    );

    const isPercentageFilled = materialOnTheFlyValue.compositions.every(
        (composition) => composition.percentage !== undefined
    );

    const hasAnyCompositionValue = materialOnTheFlyValue.compositions.some(
        (composition) => !!composition.rawMaterial
    );
    const shouldValidatePercentage = isRequired || hasAnyCompositionValue;

    const handleDeleteClick = (feId: string): void => {
        dispatchMaterialOnTheFly({
            type: 'delete_composition',
            payload: { feId },
        });
    };

    const handleCompositionChange = (
        newComposition: CompositionFeType
    ): void => {
        dispatchMaterialOnTheFly({
            type: 'update_composition',
            payload: { composition: newComposition },
        });
    };

    const handleAddCompositionClick = (): void => {
        dispatchMaterialOnTheFly({ type: 'add_composition' });
    };

    useEffect(() => {
        if (totalPercentage < 100 && buttonRef.current) {
            buttonRef.current.focus();
        }
    }, [totalPercentage]);

    return (
        <Stack>
            <Stack
                direction="row"
                spacing={10}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 16,
                }}
            >
                <FormLabel
                    filled={isRawMaterialsFilled}
                    required={isRequired}
                >
                    Raw materials
                </FormLabel>
                {shouldValidatePercentage && (
                    <FormLabel
                        filled={isPercentageFilled}
                        error={totalPercentage !== 100}
                        required={isRequired}
                    >
                        {totalPercentage === 100 && `${totalPercentage}%`}
                        {totalPercentage < 100 &&
                            `MISSING: ${100 - totalPercentage}%`}
                        {totalPercentage > 100 &&
                            `REMOVE: ${totalPercentage - 100}%`}
                    </FormLabel>
                )}
            </Stack>
            <Stack spacing={5}>
                {materialOnTheFlyValue.compositions.map((composition, i) => (
                    <RawMaterialRow
                        key={`raw-material-fields-${composition.feId}`}
                        composition={composition}
                        sx={{ mt: i === 0 ? 0 : 5 }}
                        disableDelete={disableDelete}
                        allSelectedCompositions={
                            materialOnTheFlyValue.compositions
                        }
                        onCompositionChange={handleCompositionChange}
                        onDelete={handleDeleteClick}
                    />
                ))}
                <Button
                    ref={buttonRef} // This is used to focus the button when the totalPercentage is less than 100
                    sx={{ marginRight: '64px !important' }}
                    variant="dashed"
                    disabled={disableAddRawMaterial}
                    startIcon={<PlusIcon size={16} />}
                    onClick={handleAddCompositionClick}
                >
                    Raw material
                </Button>
            </Stack>
        </Stack>
    );
};

export { RawMaterialsSection };
